import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { isNil } from 'lodash-es';
import React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { ACCOUNT_DATA } from 'src/account/my-account/graphql/account-data.query';
import { CustomFieldFormInput } from 'src/shared';
import { FieldTypesEnum } from 'src/shared/data/constants';
import { Button, Modal } from 'src/shared/ui/elements';
import { handleApiError } from 'src/shared/utils/handleApiError';
import * as Yup from 'yup';
import { ACCOUNT_PARTICIPANTS } from '../graphql/account-participants.query';
import { UPSERT_PARTICIPANT } from '../graphql/upsert-participant.mutation';
import {
  ParticipantFormFields,
  getParticipantInitialValues,
  participantSchema,
} from './participant-form-fields';

const schema = Yup.object().shape(participantSchema(false));
const getInitialValues = (participant, entityCategories = []) => {
  const values = {
    ...getParticipantInitialValues(participant),
    tshirtSize: participant?.tshirtSize || null,
    healthConditions: participant?.healthConditions || '',
  };

  if (entityCategories.length > 0) {
    for (const entityCategory of entityCategories) {
      const fieldType = entityCategory.fieldType;
      const fieldTypeEnum = FieldTypesEnum[fieldType] || {};

      const isSelect = fieldTypeEnum.category === 'select';
      const isMultiSelect = fieldTypeEnum.isMultiSelect === true;
      const isDropdown = fieldTypeEnum.isDropdown === true;

      const categoryValue =
        participant?.categoryValues?.filter(
          (cv) => +cv.entityCategoryId === +entityCategory.id,
        ) || [];

      let value;
      if (!isSelect) {
        value = categoryValue?.[0]?.value || '';
      } else if (isMultiSelect) {
        value =
          categoryValue?.map((cv) =>
            isDropdown
              ? { label: cv?.categoryOption?.name, value: cv?.categoryOptionId }
              : +cv?.categoryOptionId,
          ) || [];
      } else {
        value = isDropdown
          ? categoryValue?.[0]?.categoryOptionId
            ? {
                label: categoryValue?.[0]?.categoryOption?.name,
                value: categoryValue?.[0]?.categoryOptionId,
              }
            : null
          : categoryValue?.[0]?.categoryOptionId || null;
      }

      values[`customFields-${entityCategory.id}`] = value;
    }
  }
  return values;
};
export const ParticipantModal = ({
  $initialValues,
  $actions,
  onSave = () => {},
  customFieldCategoriesList = [],
}) => {
  const isEdit = !!$initialValues?.id;
  const formMethods = useForm({
    defaultValues: getInitialValues($initialValues, customFieldCategoriesList),
    resolver: yupResolver(schema),
  });
  const { setError, handleSubmit } = formMethods;

  const [mutate, { loading }] = useMutation(UPSERT_PARTICIPANT, {
    refetchQueries: [ACCOUNT_DATA, ACCOUNT_PARTICIPANTS],
    onError: (error) => {
      handleApiError(error, setError);
    },
    onCompleted: (result) => {
      const participantId = result?.upsertParticipant?.id;
      $actions.close();
      if (participantId) onSave(participantId);
    },
  });

  const onSubmit = async (values) => {
    const { state, gender, ...rest } = values;
    const customFields = customFieldCategoriesList
      .map((entityCategory) => {
        const fieldType = entityCategory.fieldType;
        const isSelect =
          FieldTypesEnum[fieldType].category === FieldTypesEnum.select.value;
        const isDropdown = FieldTypesEnum[fieldType].isDropdown === true;
        const value = values[`customFields-${entityCategory.id}`];
        if (isNil(value)) {
          return null;
        }
        return {
          categoryId: entityCategory.category.id,
          categoryOptionId: isSelect
            ? Array.isArray(value)
              ? isDropdown
                ? value.map((v) => v.value).filter(Boolean)
                : value.filter(Boolean)
              : isDropdown
                ? [value?.value].filter(Boolean)
                : [value].filter(Boolean)
            : [],
          fieldValue: isSelect ? null : String(value),
          id: entityCategory.id,
        };
      })
      .filter(Boolean);
    const data = {
      ...rest,
      gender: gender?.value,
      state: state?.value,
      customFieldCategoryValues: customFields,
    };

    await mutate({
      variables: {
        input: {
          id: $initialValues?.id,
          ...Object.fromEntries(
            Object.entries(data).filter(
              ([key]) => !key.includes('customFields-'),
            ),
          ),
        },
      },
    });
  };

  return (
    <Modal
      $title={`${isEdit ? 'Edit' : 'Add'} participant`}
      $actions={$actions}
    >
      <form
        onSubmit={(event) => {
          event.stopPropagation();
          handleSubmit(onSubmit)(event);
        }}
        id="participant-form"
      >
        <Modal.Content>
          <FormProvider {...formMethods}>
            <ParticipantFormFields
              eventSpecificFields={
                <>
                  <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                    {customFieldCategoriesList.map((entityCategory) => {
                      return (
                        <div key={entityCategory.id}>
                          <Controller
                            name={`customFields-${entityCategory.id}`}
                            control={formMethods.control}
                            render={({ field }) => (
                              <CustomFieldFormInput
                                {...field}
                                entityCategory={entityCategory}
                              />
                            )}
                          />
                        </div>
                      );
                    })}
                  </div>
                </>
              }
            />
          </FormProvider>
        </Modal.Content>

        <Modal.Footer className="flex align-center justify-flex-end">
          <Button
            className="alt outline"
            onClick={$actions.close}
            type="button"
            $loading={loading}
          >
            Cancel
          </Button>

          <Button
            className="ml-12"
            type="submit"
            form="participant-form"
            $loading={loading}
            $loadingText="Saving..."
          >
            Save
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
